import { WebRTCStats } from '@peermetrics/webrtc-stats';

import {
  addCallStatsSample,
  applyCallStatsAverages,
  calculateCallScore,
  convertStatsObjectToCallStats,
  createCallStatsSamples,
  getLatestConnectionMetrics,
  sendWebRTCCallScore,
} from 'features/analytics/helpers/';
import {
  DEFAULT_WEBRTC_STATS_CONFIG,
  type ConnectionInfo,
  type ConnectionMetrics,
  type WebRTCStatsData,
} from 'features/analytics/interfaces/';
import type { RootState } from 'features/app/store/store';

const DISCONNECTED_STATES = ['closed', 'failed', 'disconnected'] as const;
type DisconnectedState = (typeof DISCONNECTED_STATES)[number];
const MISSING_CONNECTION_ERROR_MESSAGE =
  'Could not find the desired connection';

export class WebRTCCallQualityAnalytics {
  private static instance: WebRTCCallQualityAnalytics | null = null;

  private readonly connections = new Map<string, ConnectionInfo>();
  private readonly dispatch: ((action: any) => void) | null;
  private readonly getState: (() => RootState) | null;
  private readonly webrtcStats: WebRTCStats;

  private constructor(
    dispatch: ((action: any) => void) | null,
    getState: (() => RootState) | null
  ) {
    this.dispatch = dispatch;
    this.getState = getState;
    this.webrtcStats = new WebRTCStats(DEFAULT_WEBRTC_STATS_CONFIG);
    // Registered once: every SIP call reuses the same peerId on this singleton.
    this.webrtcStats.on('stats', (event) => this.handleStatsEvent(event));
    this.webrtcStats.on('connection', (event) =>
      this.handleConnectionEvent(event)
    );
  }

  public static getInstance(
    dispatch?: ((action: any) => void) | null,
    getState?: (() => RootState) | null
  ): WebRTCCallQualityAnalytics {
    WebRTCCallQualityAnalytics.instance ??= new WebRTCCallQualityAnalytics(
      dispatch || null,
      getState || null
    );

    return WebRTCCallQualityAnalytics.instance;
  }

  public static destroyInstance(): void {
    if (WebRTCCallQualityAnalytics.instance) {
      WebRTCCallQualityAnalytics.instance.cleanup();
      WebRTCCallQualityAnalytics.instance = null;
    }
  }

  // Connection Management
  public addConnection(
    pc: RTCPeerConnection,
    peerId: string,
    callId?: number
  ): void {
    if (this.connections.has(peerId)) {
      console.warn(`Connection for peer ${peerId} already exists`);
      return;
    }

    const connectionInfo: ConnectionInfo = {
      peerId,
      pc,
      webrtcStats: this.webrtcStats,
      callId,
    };

    this.webrtcStats.addConnection({ pc, peerId });
    this.connections.set(peerId, connectionInfo);
  }

  public removeConnection(peerId: string): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return;
    }

    try {
      this.webrtcStats.removeConnection({ peerId, pc: connection.pc });
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes(MISSING_CONNECTION_ERROR_MESSAGE)
      ) {
        this.connections.delete(peerId);
        return;
      }

      console.error(`Error removing connection for peer ${peerId}:`, error);
    }

    this.connections.delete(peerId);
  }

  public cleanup(): void {
    // Clean up all connections first
    for (const [peerId, connection] of this.connections.entries()) {
      try {
        this.webrtcStats.removeConnection({ peerId, pc: connection.pc });
      } catch (error) {
        console.error(`Error removing connection for peer ${peerId}:`, error);
      }
    }

    this.connections.clear();

    // Destroy the WebRTCStats instance
    try {
      this.webrtcStats.destroy();
    } catch (error) {
      console.error('Error destroying WebRTCStats instance:', error);
    }
  }

  // Event Handlers
  private handleStatsEvent(event: {
    peerId: string;
    data: WebRTCStatsData;
  }): void {
    this.updateLatestStats(event.peerId, event.data);
  }

  private handleConnectionEvent(event: {
    peerId: string;
    event: string;
    data: string;
  }): void {
    if (
      event.event === 'onconnectionstatechange' &&
      this.isDisconnectedState(event.data)
    ) {
      this.handleConnectionEnd(event.peerId);
    }
  }

  private isDisconnectedState(state: string): state is DisconnectedState {
    return DISCONNECTED_STATES.includes(state as DisconnectedState);
  }

  // Stats Management
  private updateLatestStats(peerId: string, statsData: WebRTCStatsData): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return;
    }
    connection.latestStatsData = statsData;
    connection.statsSamples = addCallStatsSample(
      connection.statsSamples ?? createCallStatsSamples(),
      statsData
    );
  }

  // Call Score Calculation
  private handleConnectionEnd(peerId: string): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      console.log(`No connection found in handleConnectionEnd for ${peerId}`);
      return;
    }

    try {
      this.calculateAndNotifyScore(peerId, connection);
    } catch (error) {
      console.error(`Error calculating call score for peer ${peerId}:`, error);
    } finally {
      this.removeConnection(peerId);
    }
  }

  private calculateAndNotifyScore(
    peerId: string,
    connection: ConnectionInfo
  ): void {
    if (!connection.latestStatsData || !this.dispatch) {
      console.log(`No latestStatsData or dispatch available for ${peerId}`);
      return;
    }

    const callStats = applyCallStatsAverages(
      convertStatsObjectToCallStats(connection.latestStatsData),
      connection.statsSamples
    );
    const callScore = calculateCallScore(callStats);
    const connectionMetrics = getLatestConnectionMetrics(
      connection.latestStatsData
    );
    const connectionResolution = connectionMetrics
      ? {
          inboundVideoResolution: connectionMetrics.inboundVideoResolution,
          outboundVideoResolution: connectionMetrics.outboundVideoResolution,
        }
      : undefined;
    const voyagerForkingUsed =
      this.getState?.()?.trainingForker?.wasActiveDuringCall ?? false;
    sendWebRTCCallScore(
      peerId,
      callScore,
      this.dispatch,
      connection.callId,
      connectionResolution,
      voyagerForkingUsed
    );
  }

  // Public API Methods
  public calculateFinalScore(peerId: string): void {
    this.handleConnectionEnd(peerId);
  }

  public getLatestStatsData(peerId: string): WebRTCStatsData | null {
    return this.connections.get(peerId)?.latestStatsData ?? null;
  }

  public getAllConnections(): string[] {
    return Array.from(this.connections.keys());
  }

  public async getRawWebRTCStats(
    peerId: string
  ): Promise<RTCStatsReport | null> {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return null;
    }

    try {
      return await connection.pc.getStats();
    } catch (error) {
      console.error(
        `Error getting raw WebRTC stats for peer ${peerId}:`,
        error
      );
      return null;
    }
  }

  public getLatestConnectionMetrics(peerId: string): ConnectionMetrics | null {
    const connection = this.connections.get(peerId);
    return getLatestConnectionMetrics(connection?.latestStatsData);
  }

  public isConnectionActive(peerId: string): boolean {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return false;
    }

    return connection.pc.connectionState === 'connected';
  }

  public getActiveConnections(): string[] {
    return Array.from(this.connections.keys()).filter((peerId) =>
      this.isConnectionActive(peerId)
    );
  }
}
